import { createSelector } from 'reselect'
import { getLeAccommodationItems } from 'checkout/selectors/view/accommodation'
import getBedbankItems from 'checkout/selectors/view/items/getBedbankItems'
import { getFullOffers } from 'selectors/offerSelectors'
import { isBedbankOffer, isCruiseOffer, isTourV2Offer } from 'lib/offer/offerTypes'
import { getFlightItems } from 'checkout/selectors/view/flights'
import { getJourneyV2IdKey } from 'lib/flights/flightUtils'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getCruiseCountriesByItinerary } from 'lib/cruises/cruiseUtils'
import { findByTwoLetterCode } from 'lib/insurance/insuranceCountries'
import { EmptyArray } from 'lib/array/arrayUtils'
import { findPostPurchaseCheckout, isInsuranceItem } from 'lib/checkout/checkoutUtils'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import {
  getPassportCardQuoteItemsFromOrder,
  getTourCountryCodesInTripOrder,
  sanitizeCruiseTime,
} from 'lib/insurance/passportCardOrderQuoteItems'

function getTourV2Variation(
  offer: App.Tours.TourV2Offer,
  variationIdOrTourOptionId: string | undefined,
): App.Tours.TourV2Offer['variations'][string] | undefined {
  if (!variationIdOrTourOptionId) return undefined

  return offer.variations[variationIdOrTourOptionId] ??
    Object.values(offer.variations).find(variation => variation.fkTourOptionId === variationIdOrTourOptionId)
}

export const getPassportCardQuoteItemsFromCart = createSelector(
  (state: App.State) => getLeAccommodationItems(state),
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.offer.bedbankOffers,
  (state: App.State) => state.flights.journeysById,
  (state: App.State) => getCruiseItems(state),
  (state: App.State) => state.cruise.cruiseDeparture,
  (
    leAccommodationItems,
    bedbankItems,
    flightItems,
    tourV2Items,
    offers,
    bedbankOffers,
    journeysById,
    cruiseItems,
    cruiseDepartureMap,
  ): Array<App.PassportCardQuoteItem> => {
    const leAccommodationQuoteItems = leAccommodationItems.flatMap((item) => {
      if (item.itemType !== 'hotel' || !('checkIn' in item) || !('checkOut' in item) || !item.checkIn || !item.checkOut) {
        return []
      }

      const offer = offers[item.offerId] as App.Offer | undefined
      const geoData = offer?.property?.geoData
      const street = [geoData?.streetNumber, geoData?.route]
        .filter(Boolean)
        .join(' ')
        .trim() || offer?.property?.address || ''

      return [{
        dp: 'accommodation',
        checkin: item.checkIn,
        checkout: item.checkOut,
        accommodationType: 'HOTEL',
        name: offer?.name ?? '',
        address: {
          street,
          country: geoData?.countryCode ?? '',
          zip: '',
          city: geoData?.locality ?? '',
        },
      }]
    })

    const bedbankQuoteItems = bedbankItems.flatMap((item) => {
      if (!item.checkIn || !item.checkOut) {
        return []
      }

      const checkoutOffer = offers[item.offerId]
      const offer = (isBedbankOffer(checkoutOffer) ? checkoutOffer : undefined)
        ?? bedbankOffers[item.offerId]
      const street = [offer?.property?.address.line1, offer?.property?.address.line2]
        .filter(Boolean)
        .join(' ')
        .trim()

      return [{
        dp: 'accommodation',
        checkin: item.checkIn,
        checkout: item.checkOut,
        accommodationType: 'HOTEL',
        name: offer?.name ?? '',
        address: {
          street,
          country: offer?.property?.address.countryCode ?? '',
          zip: offer?.property?.address.postalCode ?? '',
          city: offer?.property?.address.city ?? '',
        },
      }]
    })

    const flightQuoteItems = flightItems.flatMap((item) => {
      const flights = item.flights.flatMap((flight) => {
        if (!flight.journeyId || !item.searchId) return []

        const journeyKey = getJourneyV2IdKey(flight.journeyId, item.searchId, flight.fareFamily?.id)
        const journey = journeysById[journeyKey] as App.JourneyV2 | undefined
        if (!journey?.flightGroup) return []

        return journey.flightGroup.flights.map((segment: App.FlightV2) => ({
          arrivalDate: segment.arrivalDate,
          departureDate: segment.departingDate,
          flightId: segment.flightNumber,
          departureAirport: segment.departureAirport,
          arrivalTime: segment.arrivalTime,
          departureTime: segment.departingTime,
          arrivalAirport: segment.arrivalAirport,
        }))
      })
      if (!flights.length) return []
      return [{ dp: 'flight' as const, flights }]
    })

    const tourQuoteItems = tourV2Items.flatMap((item) => {
      if (!item.startDate || !item.endDate) return []

      const offer = offers[item.offerId]
      if (!isTourV2Offer(offer)) return []

      const rootDeparture = offer.departures?.[item.purchasableOption.fkDepartureId]
      const variation = getTourV2Variation(offer, item.purchasableOption.fkVariationId) ??
        getTourV2Variation(offer, rootDeparture?.fkVariationId)
      if (!variation) return []

      const departure = variation.departures.find(d => d.id === item.purchasableOption.fkDepartureId) ??
        rootDeparture
      if (!departure) return []

      const itineraryCountryCodes = variation.itinerary
        ? getTourCountryCodesInTripOrder(variation.itinerary)
        : []
      const uniqueDestinations = [...new Set([
        variation.startLocationCountryCode,
        variation.endLocationCountryCode,
        ...itineraryCountryCodes,
      ].filter((c): c is string => !!c))]
        .map(code => findByTwoLetterCode(code))
        .filter((name): name is string => !!name)

      const departureTime = departure.startTimeLocal
      const arrivalTime = departure.endTimeLocal

      // A season authored without start/end location details leaves these unresolvable,
      // so fall back to the ends of the itinerary. uniqueDestinations is in trip order
      // (see getTourCountryCodesInTripOrder), and collapses to a single entry for a
      // single-country tour, which then serves as both ends.
      const departureCountry = findByTwoLetterCode(variation.startLocationCountryCode ?? '') ??
        uniqueDestinations[0]
      const arrivalCountry = findByTwoLetterCode(variation.endLocationCountryCode ?? '') ??
        uniqueDestinations[uniqueDestinations.length - 1]
      // With no destinations at all there is nothing left to fall back to, and
      // PassportCard rejects an empty departureCountry outright. Drop the item rather than
      // send a trip we cannot describe - no eligible items means no section, no quote.
      if (!departureCountry || !arrivalCountry) return []

      return [{
        dp: 'tour' as const,
        name: offer.name,
        departureCountry,
        arrivalCountry,
        departureDate: item.startDate,
        departureTime,
        arrivalDate: item.endDate,
        arrivalTime,
        destinations: uniqueDestinations,
      }]
    })

    const cruiseQuoteItems = cruiseItems.flatMap((item) => {
      if (!item.startDate || !item.endDate) return []

      const offer = offers[item.offerId]
      if (!isCruiseOffer(offer)) return []

      const departure = cruiseDepartureMap[item.departureId]

      const itinerary = offer.itinerary ?? []
      const portsOfCall = itinerary.filter(stop => stop.port != null)
      const firstPortStop = portsOfCall[0]
      const lastPortStop = portsOfCall[portsOfCall.length - 1]

      const countryCodes = getCruiseCountriesByItinerary(itinerary)
      const destinations = countryCodes
        .map(code => findByTwoLetterCode(code))
        .filter((name): name is string => !!name)

      const departureTime = sanitizeCruiseTime(firstPortStop?.endTime)
      const arrivalTime = sanitizeCruiseTime(lastPortStop?.startTime)

      const departureCountry = findByTwoLetterCode(firstPortStop?.port?.countryCode ?? '')
      const arrivalCountry = findByTwoLetterCode(lastPortStop?.port?.countryCode ?? '')
      // Same as the tour branch: PassportCard rejects an empty departureCountry, so an
      // itinerary without resolvable embark/disembark ports drops out entirely.
      if (!departureCountry || !arrivalCountry || !destinations.length) return []

      return [{
        dp: 'cruise' as const,
        name: offer.name,
        departureCountry,
        arrivalCountry,
        departureDate: departure?.departureDate ?? item.startDate,
        departureTime,
        arrivalDate: departure?.returnDate ?? item.endDate,
        arrivalTime,
        destinations,
      }]
    })

    return [...leAccommodationQuoteItems, ...bedbankQuoteItems, ...flightQuoteItems, ...tourQuoteItems, ...cruiseQuoteItems]
  },
)

const getPassportCardQuoteItemsFromExistingOrder = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.cruise.cruiseOfferSnapshot,
  (state: App.State) => state.orders.flightDetails,
  (order, cruiseOfferSnapshots, flightDetails): Array<App.PassportCardQuoteItem> => {
    if (!order) return EmptyArray

    return getPassportCardQuoteItemsFromOrder(order, {
      cruiseOfferSnapshots,
      flightDetailsByOrderItems: getFlightDetailsByOrderItems(order, flightDetails) ?? {},
    })
  },
)

/**
 * Trip items for the PassportCard quote. The cart describes the trip whenever it carries
 * trip items; a post-purchase insurance-only cart carries none, so when new cover is being
 * attached the existing order describes it instead. A cart updating a policy already held
 * on the order (its insurance item is bound to an order item) sends no items, keeping the
 * update quote comparable to the policy it replaces. Other post-purchase modes (date
 * changes, upgrades) keep the cart as the only source, so a new stay is never quoted
 * alongside the one it replaces.
 */
const getPassportCardQuoteItems = createSelector(
  getPassportCardQuoteItemsFromCart,
  getPassportCardQuoteItemsFromExistingOrder,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.items.some(item => isInsuranceItem(item) && !!item.orderItemId),
  (cartItems, existingOrderItems, postPurchase, isUpdatingHeldPolicy): Array<App.PassportCardQuoteItem> => {
    if (cartItems.length) return cartItems
    return postPurchase === 'insurance' && !isUpdatingHeldPolicy ? existingOrderItems : EmptyArray
  },
)

export const hasPassportCardEligibleItems = createSelector(getPassportCardQuoteItems, items => items.length > 0)

export default getPassportCardQuoteItems
